import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuditLog } from '../../api/admin';
import { Page, PageHeader } from '../../components/Layout';
import { FilterBar, FilterSelect } from '../../components/FilterBar';
import { Pagination } from '../../components/Pagination';
import { Badge } from '../../components/Badge';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { formatDateTime, formatRelative, humanizeAction } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { AuditAction } from '../../types/api';

const ACTIONS: { value: AuditAction; label: string }[] = [
  { value: 'user.created', label: 'User created' },
  { value: 'user.updated', label: 'User updated' },
  { value: 'user.activated', label: 'User restored' },
  { value: 'user.deactivated', label: 'User suspended' },
  { value: 'user.password_reset', label: 'Password reset' },
  { value: 'tutor.verified', label: 'Tutor verified' },
  { value: 'tutor.unverified', label: 'Verification removed' },
  { value: 'session.call_accessed', label: 'Administrator call access' },
  { value: 'session.cancelled', label: 'Lesson cancelled' },
  { value: 'session.tutor_changed', label: 'Lesson tutor changed' },
  { value: 'message.deleted', label: 'Message removed' },
  { value: 'impersonation.started', label: 'Impersonation started' },
  { value: 'impersonation.stopped', label: 'Impersonation stopped' },
];

/** Actions worth making visually distinct at a glance in a long list. */
const SENSITIVE: AuditAction[] = [
  'impersonation.started',
  'user.deactivated',
  'user.password_reset',
  'session.cancelled',
  'message.deleted',
];

function Metadata({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data ?? {}).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return <span className="text-ink-3">—</span>;

  return (
    <ul className="space-y-0.5">
      {entries.map(([key, value]) => {
        // `changes` is the before/after map written on an update — render it
        // as "field: old → new" rather than dumping JSON at the operator.
        if (key === 'changes' && value && typeof value === 'object') {
          return Object.entries(value as Record<string, { from?: unknown; to?: unknown }>).map(
            ([field, diff]) => (
              <li key={field} className="text-[0.6875rem]">
                <span className="text-ink-3">{field}:</span>{' '}
                <span className="text-ink-2 line-through">{String(diff?.from ?? '—')}</span>{' '}
                <span className="text-ink-3">to</span>{' '}
                <span className="text-ink">{String(diff?.to ?? '—')}</span>
              </li>
            ),
          );
        }
        return (
          <li key={key} className="text-[0.6875rem]">
            <span className="text-ink-3">{key}:</span>{' '}
            <span className="text-ink-2">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function AuditLogPage() {
  const [params, setParams] = useSearchParams();
  const targetId = params.get('targetId') ?? undefined;

  const [action, setAction] = useState<AuditAction | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const query = useMemo(
    () => ({
      page,
      limit,
      action: action || undefined,
      targetId,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(to).toISOString() : undefined,
    }),
    [page, limit, action, targetId, from, to],
  );

  const audit = useAuditLog(query);
  const hasFilters = Boolean(action || from || to || targetId);

  const resetFilters = () => {
    setAction('');
    setFrom('');
    setTo('');
    setPage(1);
    if (targetId) setParams({}, { replace: true });
  };

  return (
    <Page>
      <PageHeader
        title="Audit log"
        description="Every administrative action, in order. Entries cannot be edited or removed."
      />

      {targetId && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-accent/30 bg-accent-wash px-3 py-2 text-xs">
          <span className="text-ink-2">Showing actions against one account only.</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setParams({}, { replace: true })}
          >
            Show everything
          </button>
        </div>
      )}

      <div className="panel overflow-hidden">
        <FilterBar onReset={resetFilters} hasFilters={hasFilters}>
          <FilterSelect
            label="Action"
            value={action}
            onChange={(v) => {
              setAction(v);
              setPage(1);
            }}
            placeholder="Any action"
            options={ACTIONS}
          />
          <label className="flex items-center gap-1.5 text-xs text-ink-3">
            From
            <input
              type="date"
              className="input h-[2.125rem] w-auto py-0"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-ink-3">
            To
            <input
              type="date"
              className="input h-[2.125rem] w-auto py-0"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </FilterBar>

        {audit.isError ? (
          <ErrorState
            message={audit.error instanceof ApiError ? audit.error.message : undefined}
            onRetry={() => void audit.refetch()}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>By</th>
                    <th>Target</th>
                    <th>Details</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.isLoading ? (
                    <SkeletonRows rows={10} cols={6} />
                  ) : audit.data && audit.data.data.length > 0 ? (
                    audit.data.data.map((entry) => (
                      <tr key={entry.id}>
                        <td className="whitespace-nowrap align-top text-xs text-ink-2">
                          <span title={formatDateTime(entry.createdAt)}>
                            {formatRelative(entry.createdAt)}
                          </span>
                        </td>
                        <td className="align-top">
                          <Badge tone={SENSITIVE.includes(entry.action) ? 'live' : 'neutral'}>
                            {humanizeAction(entry.action)}
                          </Badge>
                        </td>
                        <td className="align-top">
                          <Link
                            to={`/users/${entry.adminId}`}
                            className="text-[0.8125rem] text-ink hover:text-accent hover:underline"
                          >
                            {entry.adminName}
                          </Link>
                        </td>
                        <td className="align-top text-xs">
                          {entry.targetId ? (
                            <Link
                              to={
                                entry.targetType === 'session'
                                  ? `/sessions/${entry.targetId}`
                                  : `/users/${entry.targetId}`
                              }
                              className="text-accent hover:underline"
                            >
                              {entry.targetType ?? 'record'}
                            </Link>
                          ) : (
                            <span className="text-ink-3">—</span>
                          )}
                        </td>
                        <td className="max-w-md align-top">
                          <Metadata data={entry.metadata} />
                        </td>
                        <td className="mono align-top text-[0.6875rem] text-ink-3">
                          {entry.ipAddress ?? '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="border-b-0 p-0">
                        <EmptyState
                          title="Nothing recorded"
                          description={
                            hasFilters
                              ? 'No actions match these filters.'
                              : 'Administrative actions will appear here as they happen.'
                          }
                          action={
                            hasFilters ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={resetFilters}
                              >
                                Clear filters
                              </button>
                            ) : null
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {audit.data && audit.data.data.length > 0 && (
              <Pagination
                meta={audit.data.meta}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>
    </Page>
  );
}
